import React from 'react'
import { useQuery, gql } from '@apollo/client'
import Box from '@material-ui/core/Box'
import Container from '@material-ui/core/Container'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import LinearProgress from '@material-ui/core/LinearProgress'
import CreditCardOutlinedIcon from 'components/material-symbol/icons/CreditCard'
import ContactlessOutlinedIcon from 'components/material-symbol/icons/Contactless'
import AddOutlinedIcon from 'components/material-symbol/icons/Add'
import Error from 'components/error'
import { withProtectedRoute, roles } from 'lib/auth'
import { PageHeader } from 'components/page-header'
import CircularHighlightedIcon from 'components/circular-highlighted-icon'
import TipWell from 'components/tip-well'
import PaymentAccountsDisplay from './payment-accounts-display'
import AddPaymentAccountDialog from './add-payment-account-dialog'
import useQueryString from 'lib/hooks/use-query-string'
import StripeAccountSetupRedirect from './stripe-account-setup-redirect'
import { useStyles, paymentAccountEmptyStateIconProps } from './styles'

const PAYMENTS_PATH = '/account/payments'

const paymentsCrumb = [{ label: 'Payments', to: PAYMENTS_PATH }]

const GET_PAYMENT_ACCOUNTS = gql`
  query CoachPaymentAccounts {
    currentUser {
      attributes {
        ... on Coach {
          paymentAccounts {
            id
            name
            stripeActive
            manageUrl
            detailsSubmitted
            chargesEnabled
            payoutsEnabled
          }
        }
      }
    }
  }
`

const PaymentAccountsPage = () => {
  const classes = useStyles()
  const { 'refresh-payment-account-link': accountId } = useQueryString()
  const { data, loading, error, refetch } = useQuery(GET_PAYMENT_ACCOUNTS)

  const pageContent = (() => {
    if (accountId) {
      return <StripeAccountSetupRedirect accountId={accountId} />
    }
    if (error) return <Error />
    if (loading) return <LinearProgress color="primary" />
    const paymentAccounts = data?.currentUser?.attributes?.paymentAccounts

    if (paymentAccounts.length === 0) {
      return (
        <Container maxWidth="sm">
          <Box textAlign="center" mt={4}>
            <Box mb={2} display="inline-block">
              <CircularHighlightedIcon
                icon={CreditCardOutlinedIcon}
                {...paymentAccountEmptyStateIconProps}
              />
            </Box>
            <Box mb={4}>
              <Typography variant="h5" style={{ fontWeight: 500 }} gutterBottom>
                Set up your online fees processing account
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Stripe is the online fee processor used for PGA of America
                online transactions. You will need to create a Stripe account to
                accept online payments. Please click the button below to get
                started.
              </Typography>
            </Box>
            <AddPaymentAccountDialog
              trigger={
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddOutlinedIcon />}
                >
                  Payment Account
                </Button>
              }
            />
          </Box>
        </Container>
      )
    }

    return (
      <Box className={classes.contentContainer}>
        <Box className={classes.stack}>
          <TipWell
            icon={ContactlessOutlinedIcon}
            caption={`These payment accounts are connected to Stripe, which handles all your online payments.
              Changes made to a payment account will affect all connections associated with that account.`}
          />
          <PaymentAccountsDisplay
            paymentAccounts={paymentAccounts}
            refetch={refetch}
          />
        </Box>
      </Box>
    )
  })()

  return (
    <>
      <PageHeader
        title="Online Payment Accounts"
        crumbs={paymentsCrumb}
        actions={
          <AddPaymentAccountDialog
            trigger={
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddOutlinedIcon />}
              >
                Payment Account
              </Button>
            }
          />
        }
      />
      <Box mb={6}>{pageContent}</Box>
    </>
  )
}

export default withProtectedRoute(PaymentAccountsPage, roles.COACH)
